import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ImagePlus, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STEPS = ["Type", "Vehicle", "Details", "Photos", "Review"];

export default function Sell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [brands, setBrands] = useState([]);

  const [form, setForm] = useState({
    listing_type: "sale",
    brand: "", model: "", year: new Date().getFullYear(), variant: "",
    price: "", mileage: "", transmission: "Automatic", fuel_type: "Petrol",
    body_type: "Sedan", engine_size: "", colour: "", condition: "Used",
    location: "Phnom Penh", description: "", images: [],
    daily_price: "", weekly_price: "", monthly_price: "", deposit: "",
    insurance_included: false, driver_included: false, self_drive_available: true,
    minimum_rental_days: 1, rental_terms: "", pickup_location: "", delivery_available: false,
  });

  useEffect(() => {
    api.get("/brands").then(({ data }) => setBrands(data));
  }, []);

  useEffect(() => {
    if (!user) { navigate("/login?next=/sell"); }
  }, [user, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (files) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        uploaded.push(url);
      }
      update("images", [...form.images, ...uploaded]);
      toast.success(`${uploaded.length} photo(s) uploaded`);
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const removeImage = (i) => update("images", form.images.filter((_, idx) => idx !== i));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form,
        year: Number(form.year),
        price: form.listing_type === "sale" ? Number(form.price || 0) : 0,
        mileage: form.mileage === "" ? null : Number(form.mileage),
        daily_price: form.daily_price === "" ? null : Number(form.daily_price),
        weekly_price: form.weekly_price === "" ? null : Number(form.weekly_price),
        monthly_price: form.monthly_price === "" ? null : Number(form.monthly_price),
        deposit: form.deposit === "" ? null : Number(form.deposit),
        minimum_rental_days: Number(form.minimum_rental_days || 1),
      };
      const { data } = await api.post("/listings", payload);
      toast.success("Listing submitted! Pending Car Market admin approval.");
      navigate(`/listing/${data.id}`);
    } catch (e) {
      toast.error("Submission failed: " + (e.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  const canNext = () => {
    if (step === 1) return form.brand && form.model && form.year;
    if (step === 2) {
      if (form.listing_type === "sale") return form.price && form.location;
      return form.daily_price && form.location;
    }
    return true;
  };

  return (
    <div data-testid="sell-page" className="bg-slate-50 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-cm-primary">Sell or Rent your car</h1>
        <p className="text-sm text-slate-500 mt-1">List in minutes. Admin approves before going live.</p>

        {/* Stepper */}
        <div className="mt-6 flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${i <= step ? 'bg-cm-primary text-white' : 'bg-slate-200 text-slate-500'}`}>
                {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? 'bg-cm-primary' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>
        <div className="mt-2 text-xs text-slate-500 flex justify-between">
          {STEPS.map((s) => <span key={s} className="flex-1 text-center first:text-left last:text-right">{s}</span>)}
        </div>

        {/* Step content */}
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 md:p-7">
          {step === 0 && (
            <div data-testid="sell-step-type">
              <h2 className="font-display font-bold text-xl text-cm-primary">What are you listing?</h2>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {[
                  { id: "sale", label: "For Sale", desc: "List your car for sale" },
                  { id: "rental", label: "For Rent", desc: "Rent out your car" },
                ].map(o => (
                  <button key={o.id} data-testid={`type-${o.id}`} onClick={() => update("listing_type", o.id)}
                    className={`text-left p-5 rounded-xl border-2 transition-all ${form.listing_type === o.id ? 'border-cm-accent bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="font-display font-bold text-lg text-cm-primary">{o.label}</div>
                    <div className="text-sm text-slate-500 mt-1">{o.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="sell-step-vehicle" className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Brand">
                <select data-testid="sell-brand" value={form.brand} onChange={(e) => update("brand", e.target.value)} className={inputCls}>
                  <option value="">Select brand</option>
                  {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Model"><input data-testid="sell-model" value={form.model} onChange={(e) => update("model", e.target.value)} className={inputCls} /></Field>
              <Field label="Year"><input data-testid="sell-year" type="number" value={form.year} onChange={(e) => update("year", e.target.value)} className={inputCls} /></Field>
              <Field label="Variant (optional)"><input data-testid="sell-variant" value={form.variant} onChange={(e) => update("variant", e.target.value)} className={inputCls} /></Field>
              <Field label="Body Type">
                <select data-testid="sell-body" value={form.body_type} onChange={(e) => update("body_type", e.target.value)} className={inputCls}>
                  {["Sedan","SUV","Hatchback","Pickup","Coupe","Convertible","Van"].map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Fuel Type">
                <select data-testid="sell-fuel" value={form.fuel_type} onChange={(e) => update("fuel_type", e.target.value)} className={inputCls}>
                  {["Petrol","Diesel","Hybrid","Electric"].map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Transmission">
                <select data-testid="sell-transmission" value={form.transmission} onChange={(e) => update("transmission", e.target.value)} className={inputCls}>
                  {["Automatic","Manual"].map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Condition">
                <select data-testid="sell-condition" value={form.condition} onChange={(e) => update("condition", e.target.value)} className={inputCls}>
                  {["New","Used","Excellent"].map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Colour"><input data-testid="sell-colour" value={form.colour} onChange={(e) => update("colour", e.target.value)} className={inputCls} /></Field>
              <Field label="Engine Size"><input data-testid="sell-engine" value={form.engine_size} onChange={(e) => update("engine_size", e.target.value)} className={inputCls} placeholder="e.g. 2.0L Turbo" /></Field>
            </div>
          )}

          {step === 2 && (
            <div data-testid="sell-step-details" className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {form.listing_type === "sale" ? (
                <>
                  <Field label="Sale Price (USD)"><input data-testid="sell-price" type="number" value={form.price} onChange={(e) => update("price", e.target.value)} className={inputCls} /></Field>
                  <Field label="Mileage (km)"><input data-testid="sell-mileage" type="number" value={form.mileage} onChange={(e) => update("mileage", e.target.value)} className={inputCls} /></Field>
                </>
              ) : (
                <>
                  <Field label="Daily Price (USD)"><input data-testid="sell-daily" type="number" value={form.daily_price} onChange={(e) => update("daily_price", e.target.value)} className={inputCls} /></Field>
                  <Field label="Weekly Price (USD)"><input data-testid="sell-weekly" type="number" value={form.weekly_price} onChange={(e) => update("weekly_price", e.target.value)} className={inputCls} /></Field>
                  <Field label="Monthly Price (USD)"><input data-testid="sell-monthly" type="number" value={form.monthly_price} onChange={(e) => update("monthly_price", e.target.value)} className={inputCls} /></Field>
                  <Field label="Deposit (USD)"><input data-testid="sell-deposit" type="number" value={form.deposit} onChange={(e) => update("deposit", e.target.value)} className={inputCls} /></Field>
                  <Field label="Min rental days"><input data-testid="sell-min-days" type="number" value={form.minimum_rental_days} onChange={(e) => update("minimum_rental_days", e.target.value)} className={inputCls} /></Field>
                  <Field label="Pickup location"><input data-testid="sell-pickup" value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} className={inputCls} /></Field>
                  <div className="md:col-span-2 flex flex-wrap gap-3">
                    <Toggle testid="sell-insurance" label="Insurance included" value={form.insurance_included} onChange={(v) => update("insurance_included", v)} />
                    <Toggle testid="sell-driver" label="Driver included" value={form.driver_included} onChange={(v) => update("driver_included", v)} />
                    <Toggle testid="sell-selfdrive" label="Self-drive" value={form.self_drive_available} onChange={(v) => update("self_drive_available", v)} />
                    <Toggle testid="sell-delivery" label="Delivery available" value={form.delivery_available} onChange={(v) => update("delivery_available", v)} />
                  </div>
                </>
              )}
              <Field label="Location">
                <select data-testid="sell-location" value={form.location} onChange={(e) => update("location", e.target.value)} className={inputCls}>
                  {["Phnom Penh","Siem Reap","Sihanoukville","Battambang"].map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Description">
                  <textarea data-testid="sell-description" rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} className={inputCls + " resize-none"} placeholder="Tell buyers about your car…" />
                </Field>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="sell-step-photos">
              <h2 className="font-display font-bold text-xl text-cm-primary">Add photos</h2>
              <p className="text-sm text-slate-500 mt-1">Up to 10 high-quality photos help your listing stand out.</p>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {form.images.map((src, i) => (
                  <div key={`${src}-${i}`} className="relative aspect-[4/3] rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removeImage(i)} data-testid={`remove-image-${i}`} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <label data-testid="sell-upload-trigger" className="aspect-[4/3] rounded-xl border-2 border-dashed border-slate-300 hover:border-cm-accent transition-colors flex flex-col items-center justify-center gap-2 cursor-pointer text-slate-500 hover:text-cm-accent">
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
                  <span className="text-xs font-medium">{uploading ? "Uploading…" : "Add photos"}</span>
                  <input data-testid="sell-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleUpload(Array.from(e.target.files || []))} />
                </label>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="sell-step-review">
              <h2 className="font-display font-bold text-xl text-cm-primary">Review your listing</h2>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <Item k="Type" v={form.listing_type === "sale" ? "For Sale" : "For Rent"} />
                <Item k="Vehicle" v={`${form.year} ${form.brand} ${form.model} ${form.variant}`} />
                {form.listing_type === "sale" ? (
                  <>
                    <Item k="Price" v={`$${form.price}`} />
                    <Item k="Mileage" v={form.mileage ? `${form.mileage} km` : "—"} />
                  </>
                ) : (
                  <>
                    <Item k="Daily" v={`$${form.daily_price}`} />
                    <Item k="Weekly" v={form.weekly_price ? `$${form.weekly_price}` : "—"} />
                    <Item k="Monthly" v={form.monthly_price ? `$${form.monthly_price}` : "—"} />
                  </>
                )}
                <Item k="Location" v={form.location} />
                <Item k="Photos" v={`${form.images.length} uploaded`} />
              </div>
              <p className="mt-4 text-xs text-slate-500">By submitting you agree the listing will be reviewed by CarMarket admin before being published.</p>
            </div>
          )}
        </div>

        {/* Nav buttons */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <button data-testid="sell-back" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-cm-primary text-sm font-medium disabled:opacity-40 flex items-center gap-2">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button data-testid="sell-next" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} className="px-5 py-2.5 rounded-xl bg-cm-primary text-white text-sm font-medium disabled:opacity-40 flex items-center gap-2 press-scale">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button data-testid="sell-submit" disabled={submitting} onClick={submit} className="px-5 py-2.5 rounded-xl bg-cm-accent text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2 press-scale">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Submit listing
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent focus:ring-2 focus:ring-cm-accent/10 outline-none transition-all";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!value)} className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${value ? 'bg-cm-primary text-white border-cm-primary' : 'bg-white text-slate-600 border-slate-200'}`}>
      {value ? "✓ " : ""}{label}
    </button>
  );
}

function Item({ k, v }) {
  return (
    <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200">
      <span className="text-slate-500 text-xs uppercase tracking-wider">{k}</span>
      <span className="font-medium text-cm-primary">{v}</span>
    </div>
  );
}
